import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Interruptor (toggle) del design-system `.ds`, sin Material. `ControlValueAccessor`
 * → funciona con `formControlName` / `[(ngModel)]`. Sustituye a `mat-slide-toggle`.
 *
 * Uso presentacional (sin formulario, como `mat-slide-toggle [checked] (change)`):
 * `[checked]` + `(checkedChange)` — p. ej. las celdas de la matriz de permisos.
 */
@Component({
	selector: "app-ds-toggle",
	standalone: true,
	imports: [CommonModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DsToggleComponent),
			multi: true,
		},
	],
	template: `
		<button
			type="button"
			class="ds-toggle"
			role="switch"
			[class.on]="checked"
			[attr.aria-checked]="checked"
			[disabled]="disabled"
			(click)="toggle()"
		>
			<span class="ds-toggle-track"><span class="ds-toggle-knob"></span></span>
			<span class="ds-toggle-label" *ngIf="label">{{ label }}</span>
		</button>
	`,
	styles: [
		`
			:host {
				display: inline-block;
			}
			.ds-toggle {
				display: inline-flex;
				align-items: center;
				gap: 10px;
				border: 0;
				background: transparent;
				padding: 0;
				cursor: pointer;
				font: inherit;
			}
			.ds-toggle:disabled {
				cursor: not-allowed;
				opacity: 0.6;
			}
			.ds-toggle-track {
				flex: none;
				width: 38px;
				height: 22px;
				border-radius: 999px;
				background: var(--line);
				position: relative;
				transition: background 0.15s;
			}
			.ds-toggle.on .ds-toggle-track {
				background: var(--accent);
			}
			.ds-toggle-knob {
				position: absolute;
				top: 2px;
				left: 2px;
				width: 18px;
				height: 18px;
				border-radius: 50%;
				background: #fff;
				box-shadow: 0 1px 3px rgba(16, 24, 40, 0.28);
				transition: transform 0.15s;
			}
			.ds-toggle.on .ds-toggle-knob {
				transform: translateX(16px);
			}
			.ds-toggle-label {
				font-size: 13.5px;
				color: var(--ink);
			}
		`,
	],
})
export class DsToggleComponent implements ControlValueAccessor {
	@Input() label = "";
	@Input() disabled = false;

	@Input() checked = false;
	@Output() checkedChange = new EventEmitter<boolean>();

	private onChange = (_value: boolean): void => {
		// intentional
	};
	private onTouched = (): void => {
		// intentional
	};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: boolean): void {
		this.checked = !!value;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: boolean) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	toggle(): void {
		if (this.disabled) {
			return;
		}
		this.checked = !this.checked;
		this.onChange(this.checked);
		this.checkedChange.emit(this.checked);
		this.onTouched();
	}
}
